import { useEffect, useRef, useState } from 'react';
import { App, Modal, Spin } from 'antd';
import QRCode from 'qrcode';
import { PLANS, billingApi, type PlanSpec } from './api';

const ORANGE = '#e8732a';
const POLL_MS = 2500;

function PlanCard({
  spec,
  selected,
  onSelect,
}: {
  spec: PlanSpec;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      style={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        border: `1.5px solid ${selected ? ORANGE : 'var(--border-soft, #e8e4dc)'}`,
        background: selected ? '#fff7f1' : 'var(--surface-1, #fff)',
        borderRadius: 8,
        padding: '12px 10px',
        cursor: 'pointer',
        textAlign: 'center',
      }}
    >
      {spec.badge && (
        <div
          style={{
            position: 'absolute',
            top: -9,
            left: '50%',
            transform: 'translateX(-50%)',
            background: ORANGE,
            color: '#fff',
            fontSize: 10,
            padding: '1px 7px',
            borderRadius: 8,
            whiteSpace: 'nowrap',
          }}
        >
          {spec.badge}
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 600 }}>{spec.label}</div>
      <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: ORANGE }}>
        ¥{spec.price}
      </div>
      <div style={{ fontSize: 11, color: '#9a9590', textDecoration: 'line-through' }}>
        原价{spec.origPrice}
      </div>
    </div>
  );
}

export function BuyModal({
  open,
  accountId,
  onClose,
  onPaid,
}: {
  open: boolean;
  accountId: string | null;
  onClose: () => void;
  onPaid: () => void;
}) {
  const { message } = App.useApp();
  const [picked, setPicked] = useState<PlanSpec['plan']>('1y');
  const [creating, setCreating] = useState(false);
  const [order, setOrder] = useState<{ outTradeNo: string; amount: string } | null>(null);
  const [qr, setQr] = useState('');
  const settled = useRef(false);

  // Reset transient state whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setOrder(null);
      setQr('');
      setCreating(false);
      settled.current = false;
    }
  }, [open]);

  const handlePay = async () => {
    if (!accountId) {
      message.error('未获取到用户标识，请重新打开插件');
      return;
    }
    setCreating(true);
    try {
      const o = await billingApi.order(accountId, picked);
      const dataUrl = await QRCode.toDataURL(o.qrCode, { margin: 1, width: 220 });
      setQr(dataUrl);
      setOrder({ outTradeNo: o.outTradeNo, amount: o.amount });
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  // Poll until Alipay reports success, then unlock + close.
  useEffect(() => {
    if (!order) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const r = await billingApi.queryOrder(order.outTradeNo);
        if (active && r.paid && !settled.current) {
          settled.current = true;
          message.success('支付成功，已升级高级版');
          onPaid();
          onClose();
          return;
        }
      } catch {
        /* transient; keep polling */
      }
      if (active) timer = setTimeout(tick, POLL_MS);
    };
    timer = setTimeout(tick, POLL_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [order, onPaid, onClose, message]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={380}
      title={order ? '支付宝扫码支付' : '升级高级版（限时优惠）'}
      destroyOnClose
    >
      {!order ? (
        <>
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            {PLANS.map((p) => (
              <PlanCard
                key={p.plan}
                spec={p}
                selected={p.plan === picked}
                onSelect={() => setPicked(p.plan)}
              />
            ))}
          </div>
          {/* 企业开通是这次的卖点，单独一块说清楚，不埋在权益清单里 */}
          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--accent-soft)',
              border: '1px solid rgba(188, 90, 50, .18)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>企业开通 · 一人升级，全员可用</div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>同一企业同事打开插件即可共用，无需重复付费</div>
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: '#5a5552', lineHeight: 1.7 }}>
            高级版有效期内 <b>全功能不限次</b>：模板不限数量 · 关联字段拉明细 · 批量打印 / 导出 PDF · 存回附件字段 · 团队共享模板。
          </div>
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <button
              onClick={onClose}
              style={{
                height: 32,
                padding: '0 16px',
                borderRadius: 6,
                border: '1px solid var(--border-soft, #e8e4dc)',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              取消
            </button>
            <button
              onClick={() => void handlePay()}
              disabled={creating}
              style={{
                height: 32,
                padding: '0 18px',
                borderRadius: 6,
                border: 'none',
                background: ORANGE,
                color: '#fff',
                fontWeight: 500,
                cursor: creating ? 'not-allowed' : 'pointer',
                opacity: creating ? 0.6 : 1,
              }}
            >
              {creating ? '生成中…' : '立即支付'}
            </button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '4px 0 8px' }}>
          <div style={{ fontSize: 13, color: '#5a5552', marginBottom: 12 }}>
            应付 <b style={{ color: ORANGE }}>¥{order.amount}</b>
          </div>
          <div
            style={{
              display: 'inline-flex',
              padding: 10,
              background: '#fff',
              border: '1px solid var(--border-soft, #e8e4dc)',
              borderRadius: 10,
            }}
          >
            {qr ? <img src={qr} width={200} height={200} alt="支付二维码" /> : <Spin />}
          </div>
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              color: '#5a5552',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin size="small" /> 用支付宝扫码，支付完成后自动解锁…
          </div>
          <div
            onClick={() => {
              setOrder(null);
              setQr('');
            }}
            style={{ marginTop: 14, fontSize: 12, color: '#9a9590', cursor: 'pointer' }}
          >
            ← 返回选择套餐
          </div>
        </div>
      )}
    </Modal>
  );
}
